/**
 * Zero Stock Report Page
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, AlertTriangle, Copy, Download, Search } from 'lucide-react'

import api from '@/lib/api'
import { debounce, formatDate } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { useToast } from '@/components/ui/toaster'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { ExportButtons } from '@/components/ui/export-buttons'

interface ZeroStockItem {
  id: number
  code: string
  alternative_code: string | null
  description: string
  current_stock: number
  minimum_stock: number
  maximum_stock: number
  last_sale_date: string | null
  last_purchase_date: string | null
  family_name: string | null
  provider_name: string | null
  purchase_cost: number
  sale_price: number
}

interface ZeroStockResponse {
  items: ZeroStockItem[]
  total: number
  page: number
  page_size: number
  pages: number
}

interface Family {
  id: number
  description: string
}

interface Provider {
  id: number
  name: string
}

const PAGE_SIZE = 20

export function ZeroStockReport() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const navigate = useNavigate()

  const handleCopy = (item: ZeroStockItem) => {
    const text = item.alternative_code
      ? `${item.alternative_code} ${item.description}`
      : item.description
    navigator.clipboard.writeText(text)
    toast({ title: t('common.copied') })
  }
  const [familyFilter, setFamilyFilter] = useState<string>('all')
  const [providerFilter, setProviderFilter] = useState<string>('all')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState<'code' | 'name_asc' | 'name_desc'>('code')
  const [page, setPage] = useState(1)

  const { data: families } = useQuery({
    queryKey: ['families'],
    queryFn: async () => {
      // Lista completa para el dropdown; /catalogs/families pagina a 20.
      const response = await api.get('/articles/catalogs/families')
      return (response.data as { id: number; name: string }[]).map((f) => ({
        id: f.id,
        description: f.name,
      })) as Family[]
    },
  })

  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      // Lista completa para el dropdown (hasta 1000); /catalogs/providers pagina a 20.
      const response = await api.get('/articles/catalogs/providers')
      return response.data as Provider[]
    },
  })

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
      setPage(1)
    }, 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  useEffect(() => {
    setPage(1)
  }, [familyFilter, providerFilter, sortOrder])

  // Filtros compartidos por la lista y por el export, para que el archivo salga
  // con exactamente lo que el usuario está viendo.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams()
    if (searchQuery.trim()) params.set('search', searchQuery.trim())
    if (familyFilter && familyFilter !== 'all') params.set('family_id', familyFilter)
    if (providerFilter && providerFilter !== 'all') params.set('provider_id', providerFilter)
    if (sortOrder === 'name_asc') {
      params.set('sort_by', 'name')
      params.set('sort_dir', 'asc')
    } else if (sortOrder === 'name_desc') {
      params.set('sort_by', 'name')
      params.set('sort_dir', 'desc')
    }
    return params
  }, [searchQuery, familyFilter, providerFilter, sortOrder])

  const { data, isLoading } = useQuery({
    queryKey: ['reports-zero-stock', page, searchQuery, familyFilter, providerFilter, sortOrder],
    queryFn: async () => {
      const params = buildFilterParams()
      params.set('page', page.toString())
      params.set('page_size', PAGE_SIZE.toString())
      const response = await api.get(`/reports/inventory/zero-stock?${params}`)
      return response.data as ZeroStockResponse
    },
  })

  const items = data?.items ?? []
  const totalPages = data?.pages ?? 0
  const total = data?.total ?? 0

  // El export trae TODAS las filas, no la página visible: en producción son
  // cerca de 10 000 artículos. Se pide sólo cuando el usuario lo pide, porque
  // bajarlas en cada visita al reporte sería un par de megas al pedo.
  const [exportRequested, setExportRequested] = useState(false)
  const { data: exportData, isFetching: isExporting } = useQuery({
    queryKey: ['reports-zero-stock-export', searchQuery, familyFilter, providerFilter, sortOrder],
    queryFn: async () => {
      const params = buildFilterParams()
      params.set('all', 'true')
      const response = await api.get(`/reports/inventory/zero-stock?${params}`)
      return response.data as ZeroStockResponse
    },
    enabled: exportRequested,
    staleTime: 60_000,
  })
  const exportItems = exportData?.items ?? []

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('reports.zeroStock')}</h1>
          <p className="text-sm text-muted-foreground">{t('reports.zeroStockDesc')}</p>
        </div>
        {exportItems.length > 0 ? (
          <ExportButtons
            data={exportItems}
            filename="stock-en-cero"
            columns={[
              { key: 'code', header: t('articles.code'), asText: true },
              { key: 'alternative_code', header: t('articles.alternativeCode'), format: (v) => (v ? String(v) : ''), asText: true },
              { key: 'description', header: t('articles.description') },
              { key: 'family_name', header: t('articles.family'), format: (v) => (v ? String(v) : '') },
              { key: 'provider_name', header: t('articles.provider'), format: (v) => (v ? String(v) : '') },
              { key: 'current_stock', header: t('articles.currentStock'), format: (v) => `${v ?? 0}` },
              { key: 'minimum_stock', header: t('articles.minimumStock'), format: (v) => `${v ?? 0}` },
              { key: 'maximum_stock', header: t('articles.maximumStock'), format: (v) => `${v ?? 0}` },
            ]}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-10 shrink-0"
            onClick={() => setExportRequested(true)}
            disabled={isExporting || total === 0}
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">
              {isExporting ? t('common.loading') : t('export.export')}
            </span>
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
        <Select value={familyFilter} onValueChange={setFamilyFilter}>
          <SelectTrigger>
            <SelectValue placeholder={t('reports.allFamilies')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('reports.allFamilies')}</SelectItem>
            {families?.map((f) => (
              <SelectItem key={f.id} value={String(f.id)}>
                {f.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <SearchableSelect
          options={[
            { value: 'all', label: t('reports.misc.allProviders') },
            ...(providers?.map((p) => ({ value: String(p.id), label: p.name })) ?? []),
          ]}
          value={providerFilter}
          onValueChange={(value) => setProviderFilter(value || 'all')}
          placeholder={t('reports.misc.allProviders')}
          searchPlaceholder="Buscar proveedor..."
          emptyMessage="No se encontraron proveedores"
        />

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('reports.misc.searchArticle')}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Summary */}
      <Card className="bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
            <div>
              <p className="text-2xl font-bold text-amber-600">{total}</p>
              <p className="text-sm text-muted-foreground">{t('reports.misc.needAttention')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              {t('reports.misc.zeroStockList')}
            </CardTitle>
            <Select
              value={sortOrder}
              onValueChange={(v) => setSortOrder(v as 'code' | 'name_asc' | 'name_desc')}
            >
              <SelectTrigger className="h-9 w-[150px] shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="code">{t('reports.misc.sortCode')}</SelectItem>
                <SelectItem value="name_asc">{t('reports.misc.sortNameAsc')}</SelectItem>
                <SelectItem value="name_desc">{t('reports.misc.sortNameDesc')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : items.length > 0 ? (
            <div className="space-y-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 touch-manipulation"
                >
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center shrink-0">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0 cursor-text select-text">
                    <p className="font-medium truncate">
                      {item.code}
                      {item.alternative_code && <span className="text-muted-foreground"> / {item.alternative_code}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.family_name || '-'} • {item.provider_name || '-'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-red-600">
                      Stock: {item.current_stock}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Min: {item.minimum_stock} / Max: {item.maximum_stock}
                    </p>
                    {item.last_sale_date && (
                      <p className="text-xs text-muted-foreground">
                        Ult. venta: {formatDate(item.last_sale_date)}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() => handleCopy(item)}
                    aria-label={t('common.copy')}
                    title={t('common.copy')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {t('reports.noZeroStockItems')}
            </p>
          )}

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4">
              <p className="text-sm text-muted-foreground">
                {t('common.page')} {page} {t('common.of')} {totalPages}
              </p>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
